import moment from 'moment'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import { fileUrl } from 'api/api'
import PromotionApi from 'api/application/promotion.api'
import { routeName } from 'routes/routes-name'

/** UTILS || SLICE */
import { onImgError } from 'constants/images'
import { toBuddhistYear } from 'utils/app.utils'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'

/** STYLE */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { notiError } from 'component/notifications/notifications'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import { ImageWrap } from './use-styled'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

export default function Promotions() {
  const { t } = useTranslation()
  const history = useHistory()

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [promotions, setPromotions] = useState([]);

  const loadData = async () => {
    try {
      const resp = await PromotionApi.findAll({
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setPromotions(resp.data)
      } else {
        setPromotions([])
      }
    } catch (error) {
      notiError('เกิดข้อผิดพลาด')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const onActive = () => {
    if (id) {
      PromotionApi.updatePromotionStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PROMOTIONS.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PROMOTIONS.MESSAGE.ERROR.DEFAULT'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      PromotionApi.removePromotion(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PROMOTIONS.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PROMOTIONS.MESSAGE.ERROR.DEFAULT'))
        }
      })
    }
  }

  const onCreate = () => {
    history.push(routeName.promotions + '/create')
  }

  const onEdit = (id: any) => {
    history.push(routeName.promotions + '/edit', { promotionId: id })
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: t('TABLE.NO'), width: '50px' },
    { id: 'message', disablePadding: false, label: t('PROMOTIONS.TABLE.IMAGE'), width: '150px' },
    { id: 'message', disablePadding: false, label: t('PROMOTIONS.TABLE.TITLE') },
    { id: 'message', disablePadding: false, label: t('PROMOTIONS.TABLE.DATE_START'), width: '160px' },
    { id: 'message', disablePadding: false, label: t('PROMOTIONS.TABLE.DATE_END'), width: '160px' },
    { id: 'status', disablePadding: false, label: t('PROMOTIONS.TABLE.STATUS'), width: '150px' },
    { id: 'updatedBy', disablePadding: false, label: t('PROMOTIONS.TABLE.UPDATED_BY'), width: '180px' },
    { id: 'action', disablePadding: false, label: 'Action', width: '80px' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { promotionId, image, subject, datePublic, timePublic, dateEnd, timeEnd, status: statusPromotions, updatedBy, updatedAt } = objData
    const statusBtnActive = statusPromotions === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = statusPromotions === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: promotionId,
      id: promotionId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        {
          option: 'COMPONENT', align: 'left', component: (
            <ImageWrap>
              {
                image?.filePath && image?.fileName && (
                  <img src={`${fileUrl}/${image?.filePath}/${image?.fileName}`} onError={onImgError} className="w-100" alt='' />
                )
              }
            </ImageWrap>
          )
        },
        { option: 'TEXT', align: 'left', label: subject },
        { option: 'TEXT', align: 'left', label: datePublic && datePublic !== '0000-00-00' && timePublic ? toBuddhistYear(moment(`${datePublic} ${timePublic}`), 'DD/MM/YYYY HH.mm') : '-' },
        { option: 'TEXT', align: 'left', label: dateEnd && dateEnd !== '0000-00-00' && timeEnd ? toBuddhistYear(moment(`${dateEnd} ${timeEnd}`), 'DD/MM/YYYY HH.mm') : '-' },
        { option: 'STATUS', align: 'center', label: statusPromotions },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }

    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.id)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }
  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.subject)
  }

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`PROMOTIONS.MESSAGE.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`PROMOTIONS.MESSAGE.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { subject: name, status: t(`PROMOTIONS.MESSAGE.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 132px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('PROMOTIONS.HEADER')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('PROMOTIONS.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />

              <MainButton
                title={t('PROMOTIONS.BUTTON.ADD_PROMOTIONS')}
                disabled={permissions.isCreate.disabled}
                type='primary'
                variant='contained'
                onClick={() => onCreate()}
                startIcon={<AddCircleIcon />}
              />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={promotions.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
      </Box>
    </Box>
  )
}
